import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { UserRole } from '../../types';
import { Button } from '../../components/buttons/Button';
import { Card } from '../../components/cards/Card';
import { GraduationCap, Briefcase, Home, CheckCircle2, AlertCircle, Eye, EyeOff, FileText, Lock, Loader2 } from 'lucide-react';

export const Register: React.FC = () => {
  const { register, isLandlordEnabled } = useAuth();
  const navigate = useNavigate();

  // State
  const [role, setRole] = useState<UserRole>(UserRole.STUDENT);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form Data
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    // Student specific
    institution: '',
    // Agent specific
    agencyName: '',
    licenseNumber: '',
    // Landlord specific
    propertyCount: '1'
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    if (!acceptedTerms) {
      setError("Please accept the Terms and Conditions");
      return;
    }

    setIsLoading(true);

    // Construct metadata based on role
    const metadata: any = {
      name: formData.name,
      role: role,
      phone_number: formData.phone,
    };

    if (role === UserRole.STUDENT) {
      metadata.institution = formData.institution;
    } else if (role === UserRole.AGENT) {
      metadata.agency_name = formData.agencyName;
      metadata.license_number = formData.licenseNumber;
    } else if (role === UserRole.LANDLORD) {
      metadata.property_count = formData.propertyCount;
    }

    try {
      const { error: authError } = await register(formData.email, formData.password, metadata);

      if (authError) {
        setError(authError.message || 'Registration failed.');
        setIsLoading(false);
        return;
      }

      // Success
      switch (role) {
        case UserRole.STUDENT: navigate('/student/dashboard'); break;
        case UserRole.AGENT: navigate('/agent/dashboard'); break;
        case UserRole.LANDLORD: navigate('/landlord/dashboard'); break;
        case UserRole.ADMIN: navigate('/admin/dashboard'); break;
        default: navigate('/');
      }

    } catch (err) {
      setError('An unexpected error occurred during registration.');
      setIsLoading(false);
    }
  };

  const roles = [
    {
      id: UserRole.STUDENT,
      label: 'Student',
      description: 'I want to find accommodation.',
      icon: GraduationCap
    },
    {
      id: UserRole.AGENT,
      label: 'Agent',
      description: 'I manage listings for students.',
      icon: Briefcase
    },
    {
      id: UserRole.LANDLORD,
      label: 'Landlord',
      description: 'I own property to rent out.',
      icon: Home
    }
  ].filter(r => r.id !== UserRole.LANDLORD || isLandlordEnabled);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col font-sans">

      {/* 1. Top Navigation Bar (Minimal) */}
      <nav className="bg-white border-b border-gray-200 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2 group">
            <div className="w-8 h-8 bg-[#2f5c96] rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">I</span>
            </div>
            <span className="font-bold text-xl text-[#2f5c96] tracking-tight">Indigo Beans Rooms</span>
          </Link>
          <div className="text-sm">
            Already have an account?{' '}
            <Link to="/login" className="font-semibold text-[#2f5c96] hover:text-[#c19cca] transition-colors">
              Log in
            </Link>
          </div>
        </div>
      </nav>

      <div className="flex-1 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto space-y-8">

          {/* 2. Page Header */}
          <div className="text-center">
            <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight mb-2">Create your account</h1>
            <p className="text-gray-500">Join our verified community of students and property managers.</p>
          </div>

          {/* 3. Role Selection */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {roles.map((r) => {
              const Icon = r.icon;
              const isSelected = role === r.id;
              return (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setRole(r.id)}
                  className={`relative p-4 rounded-xl border-2 text-left transition-all duration-200 flex flex-col gap-3 ${isSelected
                    ? 'border-[#2f5c96] bg-blue-50/30 shadow-md ring-1 ring-[#2f5c96]'
                    : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                    }`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isSelected ? 'bg-[#2f5c96] text-white' : 'bg-gray-100 text-gray-500'}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className={`font-bold ${isSelected ? 'text-[#2f5c96]' : 'text-gray-900'}`}>{r.label}</h3>
                    <p className="text-xs text-gray-500 mt-1">{r.description}</p>
                  </div>
                  {isSelected && (
                    <div className="absolute top-3 right-3 text-[#2f5c96]">
                      <CheckCircle2 className="w-5 h-5" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* 4. Registration Form Container */}
          <Card className="p-8 shadow-lg border-t-4 border-t-[#2f5c96]">
            <form onSubmit={handleSubmit} className="space-y-6">

              {/* 5. Common Information Block */}
              <div className="space-y-4">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide border-b border-gray-100 pb-2 mb-4">
                  Basic Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2f5c96] focus:border-transparent outline-none transition-all"
                      placeholder="e.g. John Doe"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2f5c96] focus:border-transparent outline-none transition-all"
                      placeholder="+27..."
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2f5c96] focus:border-transparent outline-none transition-all"
                    placeholder="you@example.com"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        required
                        value={formData.password}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2f5c96] focus:border-transparent outline-none transition-all"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                    <input
                      type="password"
                      name="confirmPassword"
                      required
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2f5c96] focus:border-transparent outline-none transition-all"
                    />
                  </div>
                </div>
              </div>

              {/* 6. Role-Specific Information Block */}
              {role === UserRole.STUDENT && (
                <div className="pt-4 animate-fadeIn">
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide border-b border-gray-100 pb-2 mb-4">
                    Student Information
                  </h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">University / Institution</label>
                    <input
                      type="text"
                      name="institution"
                      value={formData.institution}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2f5c96] focus:border-transparent outline-none transition-all"
                      placeholder="e.g. University of Cape Town"
                    />
                  </div>
                </div>
              )}

              {role === UserRole.AGENT && (
                <div className="pt-4 animate-fadeIn space-y-4">
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide border-b border-gray-100 pb-2 mb-4">
                    Agency Details
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Agency Name</label>
                      <input
                        type="text"
                        name="agencyName"
                        value={formData.agencyName}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2f5c96] focus:border-transparent outline-none transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">License Number</label>
                      <input
                        type="text"
                        name="licenseNumber"
                        value={formData.licenseNumber}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2f5c96] focus:border-transparent outline-none transition-all"
                      />
                    </div>
                  </div>

                  {/* Mock Upload UI */}
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                    <FileText className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">
                      Upload Proof of Registration (Optional now)
                    </p>
                    <p className="text-xs text-gray-400">PDF, JPG up to 5MB</p>
                  </div>
                </div>
              )}

              {role === UserRole.LANDLORD && (
                <div className="pt-4 animate-fadeIn space-y-4">
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide border-b border-gray-100 pb-2 mb-4">
                    Property Portfolio
                  </h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">How many properties do you own?</label>
                    <select
                      name="propertyCount"
                      value={formData.propertyCount}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2f5c96] focus:border-transparent outline-none transition-all"
                    >
                      <option value="1">1 Property</option>
                      <option value="2-5">2 - 5 Properties</option>
                      <option value="6-20">6 - 20 Properties</option>
                      <option value="20+">20+ Properties</option>
                    </select>
                  </div>

                  {/* Payment Intent Notice */}
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex gap-3">
                    <Lock className="w-5 h-5 text-yellow-600 flex-shrink-0" />
                    <p className="text-sm text-yellow-800">
                      <strong>Note:</strong> You will not be charged today. Payment information will be collected only after your account is verified by our admin team.
                    </p>
                  </div>
                </div>
              )}

              {/* 7. Terms & Consent */}
              <div className="pt-2">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-1 w-4 h-4 text-[#2f5c96] rounded border-gray-300 focus:ring-[#2f5c96]"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                  />
                  <span className="text-sm text-gray-600">
                    I agree to the <a href="#" className="text-[#2f5c96] font-semibold hover:underline">Terms and Conditions</a> and <a href="#" className="text-[#2f5c96] font-semibold hover:underline">Privacy Policy</a>.
                  </span>
                </label>
              </div>

              {/* Error Message */}
              {error && (
                <div className="rounded-md bg-red-50 p-4 border border-red-200">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <AlertCircle className="h-5 w-5 text-red-400" aria-hidden="true" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">{error}</h3>
                    </div>
                  </div>
                </div>
              )}

              {/* 8. Submission & Status Notice */}
              <div className="pt-4">
                <Button
                  type="submit"
                  className="w-full text-lg py-4 shadow-xl shadow-[#2f5c96]/10"
                  disabled={isLoading}
                >
                  {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Create Account'}
                </Button>

                <div className="mt-4 text-center">
                  {role === UserRole.STUDENT ? (
                    <p className="text-xs text-gray-500 flex items-center justify-center gap-1">
                      <CheckCircle2 className="w-3 h-3 text-green-500" />
                      Instant access to student dashboard
                    </p>
                  ) : (
                    <p className="text-xs text-gray-500 flex items-center justify-center gap-1">
                      <AlertCircle className="w-3 h-3 text-orange-500" />
                      Admin approval required before listing
                    </p>
                  )}
                </div>
              </div>

            </form>
          </Card>
        </div>
      </div>

      {/* 10. Footer (Minimal) */}
      <footer className="bg-white border-t border-gray-200 py-8 mt-auto">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-sm text-gray-400">
            &copy; {new Date().getFullYear()} Indigo Beans Rooms. All rights reserved.
          </p>
          <div className="flex justify-center gap-6 mt-4 text-sm text-gray-500">
            <a href="#" className="hover:text-gray-900">Privacy Policy</a>
            <a href="#" className="hover:text-gray-900">Terms of Service</a>
            <a href="#" className="hover:text-gray-900">Cookie Policy</a>
          </div>
        </div>
      </footer>
    </div>
  );
};